/**
 * めくり中のシート。
 *
 * 柔らかいページ: 平面を横方向に分割し、ベジェ中心線に沿って CPU で頂点を動かす（bend.ts）。
 *   表裏は頂点を共有する 2 つの Mesh（FrontSide / BackSide）で表し、裏面は UV だけ鏡像にする。
 * 硬い表紙（hardcover）: 厚みのある箱を背表紙まわりに回すだけで、頂点は触らない（FR-FLIP-07）。
 *
 * シートは 1 度だけ作って使い回す。めくるたびにマテリアルを作り直すと、そのたびに
 * シェーダが再コンパイルされ、めくり始めに数百 ms の停止が出る。
 */
import {
  BackSide,
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  FrontSide,
  Group,
  Mesh,
  MeshPhongMaterial,
  PlaneGeometry,
  Vector3,
} from 'three';
import type { Texture } from 'three';
import { columnIndices, sheetCurve } from './bend';
import type { SheetSide } from './bend';
import { FLEX, SEGMENTS } from './constants';

/** ページ面より手前に置くオフセット（z ファイティングを避ける）。 */
const SHEET_Z = 1;

/** 硬い表紙の厚み（world unit = CSS px）。 */
const COVER_DEPTH = 6;

/** 表紙の小口（背・天地）の色。 */
const EDGE_COLOR = 0xe8e6e1;

/**
 * 頂点・法線を共有し、UV だけ左右反転したジオメトリを作る。
 * 裏から見ると絵が鏡像になるのを、テクスチャを複製せずに打ち消す。
 */
function mirroredGeometry(source: PlaneGeometry): BufferGeometry {
  const geometry = new BufferGeometry();
  const index = source.getIndex();
  if (index) geometry.setIndex(index);
  geometry.setAttribute('position', source.getAttribute('position'));
  geometry.setAttribute('normal', source.getAttribute('normal'));

  const uv = source.getAttribute('uv');
  const mirrored = new Float32Array(uv.count * 2);
  for (let i = 0; i < uv.count; i += 1) {
    mirrored[i * 2] = 1 - uv.getX(i);
    mirrored[i * 2 + 1] = uv.getY(i);
  }
  geometry.setAttribute('uv', new BufferAttribute(mirrored, 2));
  return geometry;
}

export class Sheet {
  readonly group = new Group();

  private readonly frontMaterial = new MeshPhongMaterial({
    color: 0xffffff,
    shininess: 4,
    side: FrontSide,
  });
  private readonly backMaterial = new MeshPhongMaterial({
    color: 0xffffff,
    shininess: 4,
    side: BackSide,
  });
  private readonly edgeMaterial = new MeshPhongMaterial({ color: EDGE_COLOR, shininess: 2 });

  /** 柔らかいページ用（表・裏）と、硬い表紙用（厚みのある箱）。 */
  private readonly softFront: Mesh;
  private readonly softBack: Mesh;
  private readonly cover: Mesh;

  private plane: PlaneGeometry | null = null;
  private planeBack: BufferGeometry | null = null;
  private box: BoxGeometry | null = null;

  private columns: Uint16Array<ArrayBufferLike> = new Uint16Array(0);
  private readonly points: Vector3[] = Array.from({ length: SEGMENTS + 1 }, () => new Vector3());

  private width = 0;
  private height = 0;
  private side: SheetSide = 1;
  private rigid = false;

  constructor() {
    this.softFront = new Mesh(new BufferGeometry(), this.frontMaterial);
    this.softBack = new Mesh(new BufferGeometry(), this.backMaterial);
    this.cover = new Mesh(new BufferGeometry(), [
      this.edgeMaterial,
      this.edgeMaterial,
      this.edgeMaterial,
      this.edgeMaterial,
      this.frontMaterial,
      this.backMaterial,
    ]);

    this.softFront.castShadow = true;
    this.softBack.castShadow = true;
    this.cover.castShadow = true;
    // 裏面は頂点を共有していて境界球を持たないため、視錐台カリングの対象から外す。
    this.softBack.frustumCulled = false;

    this.group.add(this.softFront, this.softBack, this.cover);
    this.group.position.set(0, 0, SHEET_Z);
    this.group.visible = false;
  }

  /**
   * めくりの直前に、寸法・立つ側・硬さ・綴じ位置を合わせる（変化が無ければ作り直さない）。
   *
   * @param hingeX 綴じの x 位置（本ローカル）。見開きは本の中央（0）だが、
   *               単ページ表示では綴じがページ自身の端にある。
   */
  configure(width: number, height: number, side: SheetSide, rigid: boolean, hingeX = 0): void {
    const changed =
      width !== this.width || height !== this.height || side !== this.side || rigid !== this.rigid;

    this.width = width;
    this.height = height;
    this.side = side;
    this.rigid = rigid;

    if (changed) this.rebuild();

    this.softFront.visible = !rigid;
    this.softBack.visible = !rigid;
    this.cover.visible = rigid;
    this.group.rotation.y = 0;
    this.group.position.setX(hingeX);
    this.group.visible = true;
    this.apply(0);
  }

  /** 表（出発ページ）と裏（着地ページ）のテクスチャを設定する。 */
  setTextures(front: Texture | null, back: Texture | null): void {
    this.frontMaterial.map = front;
    this.frontMaterial.needsUpdate = true;
    this.backMaterial.map = back;
    this.backMaterial.needsUpdate = true;
  }

  /** めくり角（0 = 閉じている・π = 倒れきった）を反映する。 */
  apply(angle: number): void {
    if (this.rigid) {
      // 硬い表紙は撓まない — 背表紙まわりの回転だけ。
      this.group.rotation.y = -this.side * angle;
      return;
    }
    const plane = this.plane;
    if (!plane) return;

    const curve = sheetCurve(this.width, angle, FLEX, this.side);
    for (let i = 0; i <= SEGMENTS; i += 1) {
      curve.getPoint(i / SEGMENTS, this.points[i] as Vector3);
    }

    const position = plane.attributes.position as BufferAttribute;
    for (let v = 0; v < position.count; v += 1) {
      const point = this.points[this.columns[v] ?? 0];
      if (!point) continue;
      position.setX(v, point.x);
      position.setZ(v, point.z);
    }
    position.needsUpdate = true;
    plane.computeVertexNormals();
    plane.computeBoundingSphere();
  }

  /** めくりが終わったら隠す（作り直さない）。 */
  hide(): void {
    this.group.visible = false;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.disposeGeometries();
    this.frontMaterial.dispose();
    this.backMaterial.dispose();
    this.edgeMaterial.dispose();
  }

  /** 寸法・側・硬さが変わったときだけジオメトリを作り直す（マテリアルは持ち越す）。 */
  private rebuild(): void {
    this.disposeGeometries();

    if (this.rigid) {
      const box = new BoxGeometry(this.width, this.height, COVER_DEPTH);
      // 背表紙を原点に置く（シートは原点まわりに回る）。
      box.translate((this.side * this.width) / 2, 0, 0);
      this.box = box;
      this.cover.geometry = box;
      return;
    }

    const plane = new PlaneGeometry(this.width, this.height, SEGMENTS, 1);
    plane.translate((this.side * this.width) / 2, 0, 0);
    this.plane = plane;
    this.planeBack = mirroredGeometry(plane);

    this.softFront.geometry = plane;
    this.softBack.geometry = this.planeBack;

    const xs = (plane.attributes.position as BufferAttribute).array;
    this.columns = columnIndices(
      Array.from({ length: xs.length / 3 }, (_, i) => xs[i * 3] ?? 0),
      this.width,
      SEGMENTS,
    );
  }

  private disposeGeometries(): void {
    this.plane?.dispose();
    this.planeBack?.dispose();
    this.box?.dispose();
    this.plane = null;
    this.planeBack = null;
    this.box = null;
  }
}
